import React from 'react';
import { withFontFallback } from '@/core/utils/fontFallback';

import {
  Activity,
  Anchor,
  Award,
  Bell,
  Book,
  Bookmark,
  Briefcase,
  Calendar,
  Camera,
  Check,
  CheckCircle,
  Clock,
  Cloud,
  Code,
  Compass,
  Cpu,
  CreditCard,
  Database,
  Download,
  Edit,
  Eye,
  EyeOff,
  File,
  Filter,
  Flag,
  Folder,
  Gift,
  Globe,
  Heart,
  Home,
  Image as ImageIcon2,
  Info,
  Key,
  LifeBuoy,
  Link as LinkIcon2,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  MessageSquare,
  Mic,
  Moon,
  Monitor,
  Music,
  Paperclip,
  Phone,
  Play,
  Printer,
  Radio,
  RefreshCw,
  Save as SaveIcon2,
  Scissors,
  Search as SearchIcon,
  Settings as SettingsIcon2,
  Share,
  Share2,
  Shield,
  ShoppingBag as ShoppingBag2,
  ShoppingCart as ShoppingCart2,
  Smartphone,
  Star,
  Sun,
  Tablet,
  Tag,
  Target,
  ThumbsUp,
  Trash,
  TrendingUp,
  Truck,
  Tv,
  Umbrella,
  Unlock,
  User as UserIcon,
  UserCheck,
  UserPlus,
  Users,
  Video,
  Volume2,
  Watch,
  Wifi,
  Wind,
  Zap as ZapIcon2,
  Zap,
} from 'lucide-react';

import { getPublicMediaUrl } from '@/core/utils/media';
import { optimizedImageUrl } from '@/core/utils/optimizedImageUrl';
import { IMAGE_QUALITY } from './shared/ResponsiveImage';

export const POPULAR_ICONS: Record<string, any> = {
  Activity,
  Anchor,
  Award,
  Bell,
  Book,
  Bookmark,
  Briefcase,
  Calendar,
  Camera,
  Check,
  CheckCircle,
  Clock,
  Cloud,
  Code,
  Compass,
  Cpu,
  CreditCard,
  Database,
  Download,
  Edit,
  Eye,
  EyeOff,
  File,
  Filter,
  Flag,
  Folder,
  Gift,
  Globe,
  Heart,
  Home,
  ImageIcon2,
  Info,
  Key,
  LifeBuoy,
  LinkIcon2,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  MessageSquare,
  Mic,
  Moon,
  Monitor,
  Music,
  Paperclip,
  Phone,
  Play,
  Printer,
  Radio,
  RefreshCw,
  SaveIcon2,
  Scissors,
  Search: SearchIcon,
  SettingsIcon2,
  Share,
  Share2,
  Shield,
  ShoppingBag2,
  ShoppingCart2,
  Smartphone,
  Star,
  Sun,
  Tablet,
  Tag,
  Target,
  ThumbsUp,
  Trash,
  TrendingUp,
  Truck,
  Tv,
  Umbrella,
  Unlock,
  User: UserIcon,
  UserCheck,
  UserPlus,
  Users,
  Video,
  Volume2,
  Watch,
  Wifi,
  Wind,
  ZapIcon2,
  Zap,
};

// Converts a React inline-style object (camelCase keys, incl. CSS custom properties
// like '--hover-scale') into a CSS declaration block string, skipping unset values.
// Used to bake a block's per-viewport wrapper styles into real `@media`-gated CSS
// instead of a single JS-computed inline style — see BlockRenderer's live rendering.
export const styleObjectToCssText = (style: Record<string, any>): string =>
  Object.entries(style)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k.startsWith('--') ? k : k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}: ${v};`)
    .join(' ');

// Pixel equivalents for the Tailwind max-w-* classes actually passed as BlockRenderer's
// `maxWidthClass` prop across the app — used only to emit a raw `max-width` CSS value
// inside a generated <style> tag, never as a Tailwind class name, so no JIT-safety concern.
export const MAX_WIDTH_PX: Record<string, number> = {
  'max-w-3xl': 768,
  'max-w-4xl': 896,
  'max-w-5xl': 1024,
  'max-w-6xl': 1152,
  'max-w-7xl': 1280,
};

export const formatStyle = (val: any): string | undefined => {
  if (val === undefined || val === null || val === '' || val === 'NaN') return undefined;
  const num = Number(val);
  if (isNaN(num)) return typeof val === 'string' ? val : undefined;
  return `${num}px`;
};

export const getShadowClass = (shadow: string): string => {
  switch (shadow) {
    case 'sm':
      return 'shadow-sm';
    case 'md':
      return 'shadow-md';
    case 'lg':
      return 'shadow-lg';
    case 'xl':
      return 'shadow-xl';
    case '2xl':
      return 'shadow-2xl';
    default:
      return '';
  }
};

// Entrance animations that may also run on a NESTED container (a card, a pass, a polaroid
// frame). Top-level sections never animate, and the older presets keep ignoring containers so
// pages that had them set (with no visible effect) don't suddenly start moving.
export const CARD_ANIMATIONS = new Set(['stamp', 'wipe-up', 'swing', 'neon']);

export const getAnimationClass = (animation: string): string => {
  switch (animation) {
    case 'fade-in':
      return 'animate-fade-in';
    case 'slide-up':
      return 'animate-slide-up';
    case 'slide-down':
      return 'animate-slide-down';
    case 'slide-left':
      return 'animate-slide-left';
    case 'slide-right':
      return 'animate-slide-right';
    case 'zoom-in':
      return 'animate-zoom-in';
    case 'bounce':
      return 'animate-bounce-in';
    case 'stamp':
      return 'animate-stamp';
    case 'wipe-up':
      return 'animate-wipe-up';
    case 'swing':
      return 'animate-swing';
    case 'neon':
      return 'animate-neon';
    default:
      return '';
  }
};

// Block types whose wrapper is visibly larger than the interactive element inside it
// (button padding/margin, add-to-cart's button row) — the generic hover effect (from
// the Animacje i Efekty panel) is applied by these blocks themselves, scoped to that
// inner element, instead of by the wrapper in BlockRenderer.tsx.
export const HOVER_SCOPED_TO_INNER_ELEMENT = new Set(['button', 'product-add-to-cart']);

export const getHoverClass = (hover: string): string => {
  switch (hover) {
    case 'scale':
      return 'hover-effect-scale';
    case 'lift':
      return 'hover-effect-lift';
    case 'glow':
      return 'hover-effect-glow';
    case 'tilt':
      return 'hover-effect-tilt';
    default:
      // No hover effect means no transition. `transition-all` also animates layout properties
      // (height, margin, padding) when responsive state settles after hydration, turning one
      // correction into a stream of layout-shift entries.
      return '';
  }
};

// 'header'/'product-name' fall back to the page's heading font (globals.settings.fonts[0]);
// everything else falls back to the page's body font (fonts[1]) — see getPageFontVars.
// var(--font-heading|body, inherit) resolves to the literal `inherit` fallback when the page
// never set globals.fonts (the CSS variable is then simply undefined), so pages without a
// configured font pair render identically to before this fallback chain existed.
export const computeBaseStyles = (currentStyles: any, styles: any, glassmorphism?: boolean, blockType?: string) => {
  const isHeading = blockType === 'header' || blockType === 'product-name';
  const pageFontFallback = isHeading ? 'var(--font-heading, inherit)' : 'var(--font-body, inherit)';
  return {
    backgroundColor: (currentStyles.glassmorphism || styles.glassmorphism)
      ? 'transparent'
      : currentStyles.backgroundColor || 'transparent',
    borderRadius: (currentStyles.borderRadius || 0) + 'px',
    // withFontFallback: "'Rubik', sans-serif" → "'Rubik', 'Rubik Fallback', sans-serif", a
    // metric-matched local face so the web-font swap doesn't re-flow text (core/utils/fontFallback.ts).
    fontFamily: (currentStyles.fontFamily && currentStyles.fontFamily !== 'inherit')
      ? withFontFallback(currentStyles.fontFamily)
      : pageFontFallback,
  };
};

/**
 * CSS background-image for a block's "Gradient" background (styles.backgroundGradientEnabled).
 * gradientType: 'linear' | 'radial' | 'stripes'. Stripes are hard-edged, repeating bands of
 * gradientFrom/gradientTo, `stripeWidth` / `stripeWidth2` px wide (2nd defaults to the 1st),
 * running perpendicular to gradientAngle. Shared by the storefront wrapper and the editor preview.
 */
export function buildBackgroundGradient(s: Record<string, any>): string {
  const from = s.gradientFrom || '#fff';
  const to = s.gradientTo || '#000';
  if (s.gradientType === 'radial') {
    return `radial-gradient(circle, ${from} ${s.gradientFromPos ?? 0}%, ${to} ${s.gradientToPos ?? 100}%)`;
  }
  const angle = s.gradientAngle ?? 135;
  if (s.gradientType === 'stripes') {
    const w1 = Math.max(1, Number(s.stripeWidth) || 20);
    const w2 = Math.max(1, Number(s.stripeWidth2) || w1);
    return `repeating-linear-gradient(${angle}deg, ${from} 0 ${w1}px, ${to} ${w1}px ${w1 + w2}px)`;
  }
  return `linear-gradient(${angle}deg, ${from} ${s.gradientFromPos ?? 0}%, ${to} ${s.gradientToPos ?? 100}%)`;
}

/**
 * CSS url() for a block's background picture. With `width` (live storefront: the phone rule in
 * BlockRenderer's <style>), our own media goes through the image optimizer — at the "sharp" quality
 * and never below what a phone displays, so the picture looks the same; the optimizer doesn't
 * upscale a smaller original. Without it (desktop rule, editor) the original upload is used as is.
 * The phone used to get the original too (jeleniewraki: a 155 KB hero, the mobile LCP).
 */
function backgroundImageCss(path: string, width?: number): string {
  const absolute = getPublicMediaUrl(path);
  return `url(${width ? optimizedImageUrl(absolute, width, IMAGE_QUALITY.sharp) : absolute})`;
}

export const computeWrapperStyles = (block: any, currentStyles: any, baseStyles: any, backgroundWidth?: number) => ({
  // `font-family`/`font-weight` are CSS-inherited properties — setting them once here
  // on the block's own wrapper cascades into every nested element by default, which is
  // what lets a merchant assign a font from the Wygląd tab consistently on ANY block
  // type, not just the ones (TextBlock, HeaderBlock) that explicitly read baseStyles.
  // A block's own more specific inline style (e.g. HeroPackshotBlock's per-field font
  // pickers) still wins for that one element, same as any normal CSS override.
  fontFamily: baseStyles.fontFamily,
  fontWeight: currentStyles.fontWeight || undefined,
  marginTop: formatStyle(currentStyles.marginTop),
  marginBottom: formatStyle(currentStyles.marginBottom),
  marginLeft: currentStyles.width && (currentStyles.blockAlign === 'center' || currentStyles.blockAlign === 'right')
    ? 'auto'
    : formatStyle(currentStyles.marginLeft),
  marginRight: currentStyles.width && currentStyles.blockAlign === 'center'
    ? 'auto'
    : formatStyle(currentStyles.marginRight),
  // paddingHorizontal/paddingVertical are a shorthand pair several themes
  // (button, newsletter) author instead of the four directional keys — used
  // as a fallback so the explicit per-side keys still win when both are set.
  paddingTop: formatStyle(currentStyles.paddingTop ?? currentStyles.paddingVertical),
  paddingBottom: formatStyle(currentStyles.paddingBottom ?? currentStyles.paddingVertical),
  paddingLeft: formatStyle(currentStyles.paddingLeft ?? currentStyles.paddingHorizontal),
  paddingRight: formatStyle(currentStyles.paddingRight ?? currentStyles.paddingHorizontal),
  backgroundColor: block.type === 'button' ? 'transparent' : baseStyles.backgroundColor,
  // Image/Slide blocks read `content`/`settings.mobileImage` for their actual
  // picture, but ALSO fall back to this same `styles.backgroundImage` key for
  // legacy pages saved before that dedicated picker existed (see ImageBlock.tsx
  // and SliderBlock.tsx). Rendering it here too would double-paint the exact
  // same stale value as a literal CSS background behind a freshly-picked photo
  // the moment a merchant re-sets the content image — so these two types keep
  // this key wrapper-inert; their own optional "frame background" instead lives
  // under a completely separate settings key (settings.frameBgImage/Color),
  // which can never collide with this legacy fallback. The Slider CONTAINER
  // itself has no such content/fallback concept, so it's free to use this key.
  backgroundImage:
    block.type === 'button' || block.type === 'image' || block.type === 'slide'
      ? undefined
      : currentStyles.backgroundGradientEnabled
        ? buildBackgroundGradient(currentStyles)
        : currentStyles.backgroundImage
          // A raw stored path (e.g. "static/media/x.jpg") only resolves as a
          // background-image on whichever origin happens to serve that path —
          // it must go through the same CDN/API resolution as every other
          // media reference, or it silently 404s on the live storefront domain.
          ? backgroundImageCss(currentStyles.backgroundImage, backgroundWidth)
          : undefined,
  backgroundSize: currentStyles.backgroundSize || 'cover',
  backgroundPosition: currentStyles.backgroundPosition || 'center',
  // Unset = the browser default (repeat), as before this option existed.
  backgroundRepeat: currentStyles.backgroundRepeat || undefined,
  borderRadius:
    block.type === 'image' || block.type === 'button' ? 0 : baseStyles.borderRadius,
  borderWidth: block.type === 'button' ? 0 : formatStyle(currentStyles.borderWidth),
  borderColor: block.type === 'button' ? 'transparent' : currentStyles.borderColor,
  borderStyle: currentStyles.borderWidth && block.type !== 'button' ? 'solid' : 'none',
  width:
    block.type === 'image'
      ? undefined
      : block.type === 'separator'
        ? '100%'
        : formatStyle(currentStyles.width),
  minHeight: !['image', 'product-gallery'].includes(block.type) && currentStyles.minHeight ? formatStyle(currentStyles.minHeight) : undefined,
  maxHeight: !['image', 'product-gallery'].includes(block.type) && currentStyles.maxHeight ? formatStyle(currentStyles.maxHeight) : undefined,
  boxShadow: currentStyles.shadowEnabled
    ? `${currentStyles.shadowOffsetX || 0}px ${currentStyles.shadowOffsetY || 4}px ${currentStyles.shadowBlur || 15}px ${currentStyles.shadowSpread || 0}px ${currentStyles.shadowColor || 'rgba(0,0,0,0.2)'}`
    : undefined,
  animationDuration: currentStyles.animation ? currentStyles.animationDuration : undefined,
  animationDelay: currentStyles.animation ? currentStyles.animationDelay : undefined,
  // Static tilt for collage layouts (stickers, polaroids, passes). The individual CSS `rotate`
  // property composes with `transform`, so entrance animations and hover effects still work.
  rotate: Number(currentStyles.rotate) ? `${Number(currentStyles.rotate)}deg` : undefined,
  // 'button'/'product-add-to-cart' already render an inner <button> that's visually
  // smaller than this wrapper (which includes the block's own padding/margin) — hovering
  // anywhere in that padding used to scale/tilt/glow the whole section instead of just
  // the button. Those two types apply this same hover effect themselves, scoped to the
  // actual button element (see ButtonBlock.tsx / ProductAddToCartBlock in ProductBlocks.tsx).
  '--hover-scale': HOVER_SCOPED_TO_INNER_ELEMENT.has(block.type)
    ? undefined
    : (currentStyles.hoverScaleValue ? currentStyles.hoverScaleValue : undefined),
  '--hover-lift': HOVER_SCOPED_TO_INNER_ELEMENT.has(block.type)
    ? undefined
    : (currentStyles.hoverLiftDistance ? `${currentStyles.hoverLiftDistance}px` : undefined),
  '--hover-tilt': HOVER_SCOPED_TO_INNER_ELEMENT.has(block.type)
    ? undefined
    : (currentStyles.hoverTiltAngle ? `${currentStyles.hoverTiltAngle}deg` : undefined),
  '--hover-glow-color': HOVER_SCOPED_TO_INNER_ELEMENT.has(block.type) ? undefined : (currentStyles.hoverGlowColor || undefined),
  '--hover-glow-blur': HOVER_SCOPED_TO_INNER_ELEMENT.has(block.type)
    ? undefined
    : (currentStyles.hoverGlowBlur ? `${currentStyles.hoverGlowBlur}px` : undefined),
  '--hover-duration': HOVER_SCOPED_TO_INNER_ELEMENT.has(block.type) ? undefined : (currentStyles.hoverTransitionDuration || undefined),
} as any);

export const computeBlockStyles = (currentStyles: any, baseStyles: any) => ({
  textAlign: currentStyles.textAlign as any,
  color: currentStyles.color,
  fontFamily: baseStyles.fontFamily,
  textDecoration: currentStyles.textDecoration,
  width: currentStyles.width,
  aspectRatio: currentStyles.aspectRatio,
  objectFit: currentStyles.objectFit,
  fontSize: formatStyle(currentStyles.fontSize),
  fontWeight: currentStyles.fontWeight,
  fontStyle: currentStyles.fontStyle,
  // Browsers already kern by default ('auto') — this only needs to emit anything
  // when a merchant explicitly turns kerning OFF (some display/decorative fonts
  // look better with pairs un-adjusted at large sizes).
  fontKerning: currentStyles.kerningEnabled === false ? 'none' : undefined,
  letterSpacing: currentStyles.letterSpacing,
  lineHeight: currentStyles.lineHeight,
  textTransform: currentStyles.textTransform,
  borderRadius: formatStyle(currentStyles.borderRadius),
  textShadow: currentStyles.textShadowEnabled
    ? `${currentStyles.textShadowOffsetX || 0}px ${currentStyles.textShadowOffsetY || 2}px ${currentStyles.textShadowBlur || 4}px ${currentStyles.textShadowColor || 'rgba(0,0,0,0.3)'}`
    : undefined,
});

export const computeInnerElementStyles = (blockStyles: any): React.CSSProperties => ({
  textAlign: blockStyles.textAlign,
  fontWeight: blockStyles.fontWeight,
  fontStyle: blockStyles.fontStyle,
  textDecoration: blockStyles.textDecoration,
  color: blockStyles.color,
  fontSize: blockStyles.fontSize,
  fontFamily: blockStyles.fontFamily,
  fontKerning: blockStyles.fontKerning,
  letterSpacing: blockStyles.letterSpacing,
  lineHeight: blockStyles.lineHeight,
  textTransform: blockStyles.textTransform,
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
});

// Inner-element typography that changes a text block's size when it differs per viewport —
// exactly the properties that caused hydration-time layout shifts when rendered inline from the
// JS-detected viewport. textAlign stays a literal value on purpose: blocks read it back
// (HeaderBlock's watermark, TextBlock's fallback) and it doesn't change the block's height.
const RESPONSIVE_TYPOGRAPHY_KEYS = ['fontSize', 'fontWeight', 'fontStyle', 'fontFamily', 'letterSpacing', 'lineHeight', 'textTransform'] as const;

// Same buckets as BlockRenderer's matchMedia viewport detection (mobile ≤ 767px, tablet
// 768–1023px, desktop ≥ 1024px), so CSS picks exactly the values the JS would after hydration.
const TYPOGRAPHY_MEDIA = {
  tablet: '(min-width: 768px) and (max-width: 1023.98px)',
  desktop: '(min-width: 1024px)',
} as const;

const SAFE_CSS_VALUE = /^[^;{}<>]*$/;

/**
 * Live storefront only: for each inner-element typography property whose value differs between
 * mobile/tablet/desktop, returns an inline `var(--bt-…)` override plus the CSS that sets that
 * variable per breakpoint on the block's wrapper class. Properties that are equal everywhere, or
 * unset on some viewport (falling back to the block's own classes there), keep their literal
 * inline value — nothing changes for blocks without per-viewport typography.
 */
export function computeResponsiveTypography(block: any, styles: any): {
  inlineOverrides: Record<string, string>;
  css: (selector: string) => string;
} | null {
  const forViewport = (viewport: 'mobile' | 'tablet' | 'desktop') => {
    const merged = { ...styles, ...(styles?.[viewport] || {}) };
    return computeInnerElementStyles(computeBlockStyles(merged, computeBaseStyles(merged, styles || {}, undefined, block?.type))) as Record<string, any>;
  };
  const values = { mobile: forViewport('mobile'), tablet: forViewport('tablet'), desktop: forViewport('desktop') };
  const inlineOverrides: Record<string, string> = {};
  const declarations = { mobile: [] as string[], tablet: [] as string[], desktop: [] as string[] };

  for (const key of RESPONSIVE_TYPOGRAPHY_KEYS) {
    const m = values.mobile[key];
    const t = values.tablet[key];
    const d = values.desktop[key];
    if (m === t && t === d) continue;
    const all = [m, t, d];
    if (all.some((v) => v === undefined || v === null || v === '' || !SAFE_CSS_VALUE.test(String(v)))) continue;
    const varName = `--bt-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
    inlineOverrides[key] = `var(${varName})`;
    declarations.mobile.push(`${varName}: ${m};`);
    declarations.tablet.push(`${varName}: ${t};`);
    declarations.desktop.push(`${varName}: ${d};`);
  }

  if (Object.keys(inlineOverrides).length === 0) return null;
  return {
    inlineOverrides,
    css: (selector: string) => `
${selector} { ${declarations.mobile.join(' ')} }
@media ${TYPOGRAPHY_MEDIA.tablet} { ${selector} { ${declarations.tablet.join(' ')} } }
@media ${TYPOGRAPHY_MEDIA.desktop} { ${selector} { ${declarations.desktop.join(' ')} } }`,
  };
}

/**
 * Recursively checks if a block of the given type exists anywhere in a block tree
 * (top-level, inside containers, rows, columns, sections, tabs, etc.).
 */
export function hasBlockTypeRecursive(blocks: any[], type: string): boolean {
  if (!Array.isArray(blocks)) return false;
  return blocks.some((b: any) => {
    if (!b) return false;
    if (b.type === type) return true;
    if (Array.isArray(b.children) && hasBlockTypeRecursive(b.children, type)) return true;
    if (Array.isArray(b.blocks) && hasBlockTypeRecursive(b.blocks, type)) return true;
    if (Array.isArray(b.items) && hasBlockTypeRecursive(b.items, type)) return true;
    if (Array.isArray(b.columns)) {
      if (hasBlockTypeRecursive(b.columns, type)) return true;
      return b.columns.some((col: any) =>
        col && (
          (Array.isArray(col.children) && hasBlockTypeRecursive(col.children, type)) ||
          (Array.isArray(col.blocks) && hasBlockTypeRecursive(col.blocks, type)) ||
          (Array.isArray(col.items) && hasBlockTypeRecursive(col.items, type))
        )
      );
    }
    if (b.settings) {
      if (Array.isArray(b.settings.children) && hasBlockTypeRecursive(b.settings.children, type)) return true;
      if (Array.isArray(b.settings.blocks) && hasBlockTypeRecursive(b.settings.blocks, type)) return true;
      if (Array.isArray(b.settings.items) && hasBlockTypeRecursive(b.settings.items, type)) return true;
    }
    return false;
  });
}

/**
 * Checks if a block (e.g. description, spec, or a container dedicated to a description)
 * is functionally empty for a given product on the storefront and should collapse.
 */
export function isBlockEmptyForProduct(block: any, product: any): boolean {
  if (!product || !block) return false;

  if (block.type === 'product-description-long') {
    const text = product.description ? product.description.replace(/<[^>]*>/g, '').trim() : '';
    return !text;
  }

  if (block.type === 'product-description-short') {
    const text = product.short_description ? product.short_description.replace(/<[^>]*>/g, '').trim() : '';
    return !text;
  }

  if (block.type === 'product-spec') {
    return !Array.isArray(product.specifications) || product.specifications.length === 0;
  }

  const hasNoAttachments = !Array.isArray(product.attachments) || product.attachments.length === 0;
  if (block.type === 'product-downloads') {
    return hasNoAttachments;
  }

  if (block.type === 'container') {
    const children = block.children || [];
    if (children.length === 0) return false;

    const hasLongDesc = children.some((c: any) => c.type === 'product-description-long');
    if (hasLongDesc) {
      const descText = product.description ? product.description.replace(/<[^>]*>/g, '').trim() : '';
      if (!descText) {
        // Kontener opisu - jeśli pozostałe elementy to tylko nagłówki/teksty dekoracyjne opisu lub spacery, cały kontener jest pusty
        const hasOtherMeaningfulContent = children.some(
          (c: any) => !['product-description-long', 'header', 'heading', 'text', 'divider', 'spacer'].includes(c.type)
        );
        if (!hasOtherMeaningfulContent) return true;
      }
    }

    // A "Do pobrania" section (heading + downloads block) disappears with no files, heading included.
    if (hasNoAttachments && children.some((c: any) => c.type === 'product-downloads')) {
      const hasOtherMeaningfulContent = children.some(
        (c: any) => !['product-downloads', 'header', 'heading', 'text', 'divider', 'spacer'].includes(c.type)
      );
      if (!hasOtherMeaningfulContent) return true;
    }
  }

  return false;
}

